import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, formatRelative } from '@/lib/api';
import { useAuth, isAdmin } from '@/contexts/AuthContext';
import { Bell, BellRing, CheckCheck, Inbox } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';

export default function NotificationsBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  const loadCount = useCallback(async () => {
    try { const r = await api.unreadCount(); setCount(r.count || 0); } catch { /* noop */ }
  }, []);
  const loadItems = useCallback(async () => {
    try { setItems(await api.listNotifications()); } catch { /* noop */ }
  }, []);

  useEffect(() => {
    if (!user) return;
    loadCount();
    const t = setInterval(loadCount, 30000);
    return () => clearInterval(t);
  }, [user, loadCount]);

  useEffect(() => { if (open) loadItems(); }, [open, loadItems]);

  const handleClick = async (n) => {
    setOpen(false);
    if (!n.read) { await api.markNotificationRead(n.id); loadCount(); }
    if (n.link) navigate(n.link);
  };

  const markAll = async () => {
    await api.markAllNotificationsRead();
    loadCount(); loadItems();
  };

  if (!user) return null;
  // Notifiche per admin (registrazioni agenti, ecc.) ma teniamo il bell per tutti perché in futuro arriveranno altre notifiche
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="icon" className="relative" data-testid="notifications-bell">
          {count > 0 ? <BellRing className="size-4" /> : <Bell className="size-4" />}
          {count > 0 && (
            <span className="absolute -top-1 -right-1 size-4 rounded-full bg-destructive text-destructive-foreground text-[10px] font-semibold grid place-items-center" data-testid="notifications-badge">{count > 9 ? '9+' : count}</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[360px] p-0">
        <div className="flex items-center justify-between p-3 border-b">
          <div className="font-medium text-sm">Notifiche {count > 0 && <Badge variant="secondary" className="ml-2">{count}</Badge>}</div>
          {count > 0 && <Button variant="ghost" size="sm" onClick={markAll} data-testid="notifications-mark-all"><CheckCheck className="size-4 mr-1" />Tutte lette</Button>}
        </div>
        <ScrollArea className="max-h-[400px]">
          {items.length === 0 ? (
            <div className="py-10 text-center"><Inbox className="size-6 mx-auto text-muted-foreground mb-2" /><div className="text-xs text-muted-foreground">Nessuna notifica</div></div>
          ) : (
            <div className="divide-y">
              {items.map(n => (
                <button key={n.id} onClick={() => handleClick(n)} className={`w-full text-left p-3 hover:bg-secondary/40 transition-colors ${!n.read ? 'bg-secondary/30' : ''}`} data-testid={`notification-${n.id}`}>
                  <div className="flex items-start gap-2">
                    {!n.read && <div className="mt-1.5 size-2 rounded-full bg-primary shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">{n.title}</div>
                      {n.message && <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.message}</div>}
                      <div className="text-[10px] text-muted-foreground mt-1">{formatRelative(n.created_at)}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
